import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ContractForm } from "./contract-form";
import { SentEmails } from "./sent-emails";
import { stepEmailBadge, type SentEmail } from "../../shared/sent-emails";
import { GuestFinalize } from "./guest-finalize";
import { eventTypeLabel, OCCASION_CHOICES } from "../../shared/event-types";
import {
  agreementFromSources,
  cardStepOf,
  emptyAgreement,
  emptyParty,
  followAgreement,
  clientPhaseLabel,
  eventCardStep,
  RELATION_OPTIONS,
  relationSelection,
  relationStored,
  type Attendance,
  type InterestedParty,
  type LeadAgreement,
  type LeadStep,
} from "../../shared/inquiry";
import { hasPermission } from "../../shared/permissions";
import { houseBrand, THANKS_MENUS, thanksMessage, type ThanksStatus } from "../../shared/thanks";
import { PhoneError, parsePhone, sanitizePhoneInput } from "../../shared/phone";
import {
  applyFollowUp,
  emptyDraft,
  emptyFlow,
  emptySnapshot,
  flowDurationLabel,
  followUpCaption,
  interestDateLabel,
  visibleInterestDates,
  parseTableNumber,
  prefillTasting,
  tableNumberUsesNumberInput,
  TastingInputError,
  type ChurchChoice,
  type CrmSnapshot,
  type EvaluationCode,
  type FlowStage,
  type InterestDate,
  type MenuChoice,
  type ServiceStyle,
  type TastingDraft,
  type VenueChoice,
  DJ_CHOICES,
  DJ_LABELS,
  type DjChoice,
} from "../../shared/tasting";
import { civilToday } from "../../shared/time";
import { foldVenue } from "../../shared/workbook";
import { ApiError, api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf } from "./ui";
import { ConflictHint, useVenueConflicts, VenueConflictWarning } from "./venue-conflicts";
import { ClashSuggestions, useClashSuggestions, type SuggestionPick } from "./clash-suggestions";
import { conflictSlotDates } from "../../shared/venue-conflicts";

type Visit = TastingDraft & {
  id: string;
  event_id: string;
  status: "open" | "archived";
  version: number;
  step: LeadStep;
  parties: InterestedParty[];
  attended: Attendance | null;
  updated_at: string;
  thanks: ThanksStatus | null;
};

type LeadEvent = { id: string; title: string; sales_stage?: string | null };

type LeadPayload = {
  /** Set when the card is opened from an event: the inquiry a new card should belong to. */
  intake_id?: string | null;
  display_subject: string;
  event_version: number | null;
  event: LeadEvent | null;
  snapshot: CrmSnapshot;
  visit: Visit | null;
};

type Sheet = {
  visit: Visit;
  event_version: number;
  event: LeadEvent;
  snapshot: CrmSnapshot;
};

const VENUES: { value: VenueChoice; label: string }[] = [
  { value: "casa", label: "CASA" },
  { value: "ktema", label: "ΚΤΗΜΑ" },
  { value: "regina", label: "REGINA" },
  { value: "other", label: "ΑΛΛΟ" },
];
const STYLES: { value: ServiceStyle; label: string }[] = [
  { value: "buffet", label: "buffet" },
  { value: "cocktail", label: "Cocktail" },
  { value: "other", label: "Άλλο" },
];
const MENUS: { value: MenuChoice; label: string }[] = [
  { value: "comfort", label: "Comfort" },
  { value: "social", label: "Social" },
  { value: "nagia", label: "Nagia" },
  { value: "other", label: "Άλλο" },
];
const RATINGS: { value: EvaluationCode; label: string; hint: string }[] = [
  { value: "TP", label: "TP", hint: "top" },
  { value: "HP", label: "HP", hint: "high" },
  { value: "MP", label: "MP", hint: "medium" },
  { value: "LP", label: "LP", hint: "low" },
  { value: "VLP", label: "VLP", hint: "very low" },
  { value: "OTHER", label: "OTHER", hint: "άλλο" },
];
const FLOW: { stage: FlowStage; label: string }[] = [
  { stage: "sales", label: "ΠΩΛΗΣΕΙΣ" },
  { stage: "tour", label: "ΠΕΡΙΗΓΗΣΗ" },
  { stage: "food", label: "ΦΑΓΗΤΟ" },
  { stage: "dessert", label: "ΓΛΥΚΟ" },
  { stage: "chef", label: "CHEF/ΑΛΛΟ" },
];
const TYPE_OPTIONS = OCCASION_CHOICES.map(([value, label]) => ({ value, label }));

export function LeadStepPage() {
  const { intakeId, visitId, eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const canWrite = hasPermission(user.role, user.permissions, "events.write");
  const [draft, setDraft] = useState<TastingDraft>(emptyDraft);
  const [parties, setParties] = useState<InterestedParty[]>([emptyParty()]);
  const [guestRaw, setGuestRaw] = useState("");
  const [peopleRaw, setPeopleRaw] = useState("");
  const [step, setStep] = useState<LeadStep>(1);
  const [progress, setProgress] = useState<LeadStep>(1);
  const [attended, setAttended] = useState<Attendance | null>(null);
  const [agreement, setAgreement] = useState<LeadAgreement>(emptyAgreement());
  const [venues, setVenues] = useState<{ id: string; name: string }[]>([]);
  const [subject, setSubject] = useState(intakeId || eventId ? "" : "Νέα εκδήλωση");
  const [eventTitle, setEventTitle] = useState("");
  const [salesStage, setSalesStage] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"open" | "archived">("open");
  const [thanks, setThanks] = useState<ThanksStatus | null>(null);
  const [sendingMail, setSendingMail] = useState(false);
  const [cardId, setCardId] = useState<string | null>(visitId ?? null);
  const [linkedEventId, setLinkedEventId] = useState<string | null>(null);
  const [emails, setEmails] = useState<SentEmail[]>([]);
  const [emailsTick, setEmailsTick] = useState(0);
  const draftRef = useRef(draft);
  const partiesRef = useRef(parties);
  const stepRef = useRef(step);
  const attendedRef = useRef<Attendance | null>(attended);
  const agreementRef = useRef(agreement);
  const derivedRef = useRef<LeadAgreement>(emptyAgreement());
  const snapshotRef = useRef<CrmSnapshot>(emptySnapshot());
  const venuesRef = useRef(venues);
  const visitIdRef = useRef<string | null>(visitId ?? null);
  const eventIdRef = useRef<string | null>(null);
  const leadIntakeRef = useRef<string | null>(intakeId ?? null);
  const eventVersionRef = useRef<number | null>(null);
  const versionRef = useRef<number | null>(null);
  const progressRef = useRef<LeadStep>(1);
  const savedSerial = useRef("");
  const guestInvalid = useRef(false);
  const saving = useRef(false);
  draftRef.current = draft;
  partiesRef.current = parties;
  stepRef.current = step;
  attendedRef.current = attended;
  agreementRef.current = agreement;
  venuesRef.current = venues;
  const canEdit = canWrite && status === "open";
  const conflicts = useVenueConflicts(ready ? draft.venue_choice : null, draft.interest_dates, linkedEventId, cardId);
  const suggestions = useClashSuggestions(ready ? draft.venue_choice : null, draft.interest_dates, linkedEventId, cardId);
  const conflictSlots = new Map(conflictSlotDates(draft.interest_dates).map((entry) => [entry.date, entry.slot]));
  const dirty = ready && canEdit && JSON.stringify({ draft, parties, attended, agreement }) !== savedSerial.current;

  useEffect(() => {
    let cancel = false;
    void api<{ venues: { id: string; name: string }[] }>("/api/venues")
      .then((result) => {
        if (!cancel) setVenues(sortVenues(result.venues));
      })
      .catch(() => {
        if (!cancel) setVenues([]);
      });
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    let cancel = false;
    setReady(false);
    const load = async (): Promise<LeadPayload | null> => {
      if (intakeId) return api<LeadPayload>(`/api/inquiries/${intakeId}`);
      if (visitId) return sheetToLead(await api<Sheet>(`/api/tastings/${visitId}`));
      if (eventId) return api<LeadPayload>(`/api/events/${encodeURIComponent(eventId)}/lead`);
      return null;
    };
    void load()
      .then((result) => {
        if (cancel) return;
        if (!result) {
          adopt(emptyDraft(), [emptyParty()], 1, null, emptySnapshot(), blankLead());
        } else {
          const next = result.visit ? pick(result.visit) : prefillTasting(result.snapshot);
          const people = result.visit?.parties?.length ? result.visit.parties : [partyFrom(next)];
          adopt(next, people, leadStepOf(result), result.visit?.attended ?? null, result.snapshot, result);
        }
        setReady(true);
      })
      .catch((reason: unknown) => {
        if (cancel) return;
        setError(messageOf(reason));
        setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [intakeId, visitId, eventId]);

  useEffect(() => {
    if (!ready || step >= 3) return;
    const derived = deriveAgreement(draftRef.current, snapshotRef.current, venues);
    setAgreement((current) => {
      const next = followAgreement(current, derivedRef.current, derived);
      derivedRef.current = derived;
      if (JSON.stringify(next) === JSON.stringify(current)) return current;
      const previousSerial = JSON.stringify({ draft: draftRef.current, parties: partiesRef.current, attended: attendedRef.current, agreement: current });
      if (previousSerial === savedSerial.current) {
        savedSerial.current = JSON.stringify({ draft: draftRef.current, parties: partiesRef.current, attended: attendedRef.current, agreement: next });
      }
      agreementRef.current = next;
      return next;
    });
  }, [ready, step, draft, venues]);

  useEffect(() => {
    if (step >= 3) return;
    setPeopleRaw(agreement.guests == null ? "" : String(agreement.guests));
  }, [agreement.guests, step]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (phase !== "saved") return;
    const timer = window.setTimeout(() => setPhase((current) => (current === "saved" ? "idle" : current)), 2200);
    return () => window.clearTimeout(timer);
  }, [phase]);

  function adopt(next: TastingDraft, people: InterestedParty[], nextStep: LeadStep, presence: Attendance | null, snapshot: CrmSnapshot, result: LeadPayload) {
    const derived = deriveAgreement(next, snapshot, venuesRef.current);
    draftRef.current = next;
    partiesRef.current = people;
    stepRef.current = nextStep;
    attendedRef.current = presence;
    agreementRef.current = derived;
    derivedRef.current = derived;
    snapshotRef.current = snapshot;
    progressRef.current = nextStep;
    savedSerial.current = JSON.stringify({ draft: next, parties: people, attended: presence, agreement: derived });
    eventIdRef.current = result.event?.id ?? null;
    if (!intakeId) leadIntakeRef.current = result.intake_id ?? null;
    setLinkedEventId(result.event?.id ?? null);
    eventVersionRef.current = result.event_version;
    visitIdRef.current = result.visit?.id ?? null;
    setCardId(result.visit?.id ?? null);
    versionRef.current = result.visit?.version ?? null;
    setDraft(next);
    setParties(people);
    setGuestRaw(next.guest_count == null ? "" : String(next.guest_count));
    setPeopleRaw(derived.guests == null ? "" : String(derived.guests));
    setStep(nextStep);
    setProgress(nextStep);
    setAttended(presence);
    setAgreement(derived);
    setSubject(result.display_subject || "Νέα εκδήλωση");
    setEventTitle(result.event?.title ?? "");
    setSalesStage(result.event?.sales_stage ?? null);
    setStatus(result.visit?.status ?? "open");
    setThanks(result.visit?.thanks ?? null);
    guestInvalid.current = false;
  }

  function patch(partial: Partial<TastingDraft>) {
    setDraft((current) => applyFollowUp(current, partial));
  }

  function onGuests(value: string) {
    const digits = value.replace(/\D/g, "");
    setGuestRaw(digits);
    if (!digits) {
      guestInvalid.current = false;
      patch({ guest_count: null });
      return;
    }
    const count = Number(digits);
    if (count < 1 || count > 100000) {
      guestInvalid.current = true;
      setError("Οι καλεσμένοι γράφονται ως ακέραιος αριθμός.");
      return;
    }
    guestInvalid.current = false;
    setError(null);
    patch({ guest_count: count });
  }

  function onPeople(value: string) {
    const digits = value.replace(/\D/g, "");
    setPeopleRaw(digits);
    if (!digits) {
      guestInvalid.current = false;
      setAgreement((current) => ({ ...current, guests: null }));
      return;
    }
    const count = Number(digits);
    if (count < 1 || count > 100000) {
      guestInvalid.current = true;
      setError("Τα άτομα γράφονται ως ακέραιος αριθμός.");
      return;
    }
    guestInvalid.current = false;
    setError(null);
    setAgreement((current) => ({ ...current, guests: count }));
    patch({ guest_count: count });
  }

  function setParty(index: number, partial: Partial<InterestedParty>) {
    setParties((current) => current.map((party, partyIndex) => (partyIndex === index ? { ...party, ...partial } : party)));
    if (index === 0) {
      setDraft((current) => ({
        ...current,
        given_name: partial.given_name ?? current.given_name,
        family_name: partial.family_name ?? current.family_name,
        phone: "phone" in partial ? partial.phone ?? null : current.phone,
        email: "email" in partial ? partial.email ?? null : current.email,
        special_requests: "notes" in partial ? partial.notes ?? null : current.special_requests,
      }));
    }
  }

  function serialOf(): string {
    return JSON.stringify({ draft: draftRef.current, parties: partiesRef.current, attended: attendedRef.current, agreement: agreementRef.current });
  }

  async function save() {
    if (!canWrite || status !== "open" || saving.current || stepRef.current >= 4) return;
    const people = partiesRef.current;
    const primary = people[0];
    if (!primary?.given_name.trim() || !primary.family_name.trim()) {
      setError("Το όνομα και το επώνυμο είναι υποχρεωτικά.");
      return;
    }
    try {
      for (const party of people) parsePhone(party.phone);
    } catch (reason) {
      if (reason instanceof PhoneError) {
        setError(reason.message);
        return;
      }
      throw reason;
    }
    if (guestInvalid.current) return;
    try {
      parseTableNumber(draftRef.current.table_number);
    } catch (reason) {
      if (reason instanceof TastingInputError) {
        setError(reason.message);
        return;
      }
      throw reason;
    }
    const view = stepRef.current;
    const advance = (view + 1) as LeadStep;
    const store = Math.max(progressRef.current, advance) as LeadStep;
    let currentDraft = draftRef.current;
    const currentAgreement = agreementRef.current;
    if (view >= 3 && currentAgreement.guests != null) currentDraft = { ...currentDraft, guest_count: currentAgreement.guests };
    draftRef.current = currentDraft;
    const serial = serialOf();
    if (serial === savedSerial.current && progressRef.current >= store) {
      setStep(advance);
      setPhase("saved");
      setError(null);
      return;
    }
    saving.current = true;
    setPhase("saving");
    setError(null);
    const body = {
      ...currentDraft,
      given_name: primary.given_name.trim(),
      family_name: primary.family_name.trim(),
      phone: primary.phone,
      email: primary.email,
      special_requests: primary.notes,
    };
    try {
      const payload: Record<string, unknown> = {
        ...body,
        interest_dates: body.interest_dates.filter((row) => row.date),
        parties: people,
        step: store,
      };
      if (view >= 2) payload.attended = attendedRef.current;
      if (view >= 3 || progressRef.current >= 3) payload.agreement = currentAgreement;
      if (leadIntakeRef.current) payload.intake_id = leadIntakeRef.current;
      if (eventIdRef.current) {
        payload.event_id = eventIdRef.current;
        payload.event_version = eventVersionRef.current;
      }
      if (versionRef.current != null) payload.version = versionRef.current;
      const result = visitIdRef.current
        ? await api<Sheet>(`/api/tastings/${visitIdRef.current}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await api<Sheet>("/api/tastings", { method: "POST", body: JSON.stringify(payload) });
      visitIdRef.current = result.visit.id;
      versionRef.current = result.visit.version;
      eventVersionRef.current = result.event_version;
      eventIdRef.current = result.event.id;
      setLinkedEventId(result.event.id);
      progressRef.current = cardStepOf(result.visit.step, result.visit.step);
      attendedRef.current = result.visit.attended;
      snapshotRef.current = result.snapshot;
      stepRef.current = advance;
      if (serialOf() === serial) savedSerial.current = serial;
      setEventTitle(result.event.title);
      setProgress(progressRef.current);
      setAttended(result.visit.attended);
      setThanks(result.visit.thanks ?? null);
      setStep(advance);
      setPhase("saved");
      if (!intakeId && !visitId && !eventId) navigate(`/lead/${result.visit.id}`, { replace: true });
    } catch (reason: unknown) {
      setPhase("idle");
      setError(messageOf(reason));
      if (reason instanceof ApiError && reason.status === 409) {
        const fresh = await reloadLead().catch(() => null);
        if (fresh) {
          const next = fresh.visit ? pick(fresh.visit) : prefillTasting(fresh.snapshot);
          const peopleNext = fresh.visit?.parties?.length ? fresh.visit.parties : [partyFrom(next)];
          adopt(next, peopleNext, leadStepOf(fresh), fresh.visit?.attended ?? null, fresh.snapshot, fresh);
        }
      }
    } finally {
      saving.current = false;
    }
  }

  async function reloadLead(): Promise<LeadPayload | null> {
    if (intakeId) return api<LeadPayload>(`/api/inquiries/${intakeId}`);
    if (eventId) return api<LeadPayload>(`/api/events/${encodeURIComponent(eventId)}/lead`);
    if (visitIdRef.current) return sheetToLead(await api<Sheet>(`/api/tastings/${visitIdRef.current}`));
    return null;
  }

  async function sendNow() {
    const id = visitIdRef.current;
    if (!id || !canEdit || sendingMail) return;
    setError(null);
    setSendingMail(true);
    try {
      const result = await api<{ thanks: ThanksStatus | null }>(`/api/tastings/${id}/thanks/send`, { method: "POST", body: "{}" });
      setThanks(result.thanks);
      refreshEmails();
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setSendingMail(false);
    }
  }

  const emailEventId = eventId ?? linkedEventId;
  useEffect(() => {
    if (!ready) return;
    const url = cardId
      ? `/api/tastings/${encodeURIComponent(cardId)}/emails`
      : emailEventId && user.role !== "reception"
        ? `/api/events/${encodeURIComponent(emailEventId)}/emails`
        : null;
    if (!url) {
      setEmails([]);
      return;
    }
    let live = true;
    api<{ emails: SentEmail[] }>(url)
      .then((result) => {
        if (live) setEmails(result.emails ?? []);
      })
      .catch(() => {
        if (live) setEmails([]);
      });
    return () => {
      live = false;
    };
  }, [ready, cardId, emailEventId, user.role, emailsTick, thanks?.state, thanks?.sent_at]);
  const refreshEmails = () => setEmailsTick((tick) => tick + 1);

  const extraOccasion = draft.occasion && !OCCASION_CHOICES.some(([value]) => value === draft.occasion) ? draft.occasion : null;
  const typeValue = agreement.event_type ?? "";
  const knownType = TYPE_OPTIONS.some((option) => option.value === typeValue);

  return (
    <div className="tasting-page">
      <header className="page-head lead-head">
        <div className="lead-head-text">
          <p className="meta">
            {eventId ? <Link to="/pipeline">Εκδηλώσεις</Link> : <Link to="/inbox">Εισερχόμενα</Link>}
            {linkedEventId && user.role !== "reception" ? <> · <Link to={`/events/${linkedEventId}`}>Λεπτομέρειες εκδήλωσης</Link></> : null}
          </p>
          <p className="lead-step">{clientPhaseLabel(step, salesStage)}</p>
          <h1>Στοιχεία εκδήλωσης</h1>
          <p className="lede">{subject || eventTitle || "Νέα εκδήλωση"}</p>
        </div>
        <nav className="lead-steps" aria-label="Βήματα">
          {([1, 2, 3, 4] as const).map((item) => {
            const locked = item > progress;
            const mail = stepEmailBadge(emails, item);
            return (
              <button
                key={item}
                type="button"
                className={`lead-step-pill s${item}`}
                aria-pressed={item === step}
                aria-current={item === step ? "step" : undefined}
                disabled={locked}
                title={locked ? "Κλειδωμένο: αποθηκεύστε πρώτα το προηγούμενο βήμα" : mail ?? undefined}
                onClick={() => goToStep(item)}
              >
                {locked ? (
                  <svg className="lock" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                    <path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <rect x="3.25" y="7" width="9.5" height="7" rx="1.8" fill="currentColor" />
                  </svg>
                ) : null}
                {item}/4
                {mail ? <span className="pill-mail" aria-label={mail}>✉</span> : null}
              </button>
            );
          })}
        </nav>
      </header>
      <Banner error={error} />
      {ready && (cardId || emails.length > 0) ? <SentEmails emails={emails} step={step} /> : null}
      {!canWrite ? <p className="banner">Αυτή η σύνδεση διαβάζει τη φόρμα. Δεν την αλλάζει.</p> : null}
      {!ready ? <p>Φόρτωση φόρμας…</p> : null}
      {ready ? (
        <>
          {step === 2 ? (
            <article className="panel lead-section">
              <h2>Παρουσία</h2>
              <div className="choice-row">
                <Choice label="Ήρθε" pressed={attended === "came"} disabled={!canEdit} onClick={() => setAttended(attended === "came" ? null : "came")} />
                <Choice label="Δεν ήρθε" pressed={attended === "missed"} disabled={!canEdit} onClick={() => setAttended(attended === "missed" ? null : "missed")} />
                <Choice label="Αναβολή" pressed={attended === "postponed"} disabled={!canEdit} onClick={() => setAttended(attended === "postponed" ? null : "postponed")} />
              </div>
              <h2>ΑΡΙΘΜΟΣ ΤΡΑΠΕΖΙΟΥ</h2>
              <label className="table-number">Τραπέζι
                <input
                  type={tableNumberUsesNumberInput(draft.table_number) ? "number" : "text"}
                  inputMode="numeric"
                  min={1}
                  max={60}
                  step={1}
                  value={draft.table_number ?? ""}
                  disabled={!canEdit}
                  onChange={(event) => patch({ table_number: event.target.value || null })}
                />
              </label>
              <p className="meta">ΚΑΤΩΤΕΡΩ Η ΡΟΗ ΤΗΣ ΕΠΙΣΚΕΨΗΣ</p>
              <div className="flow-grid">
                {FLOW.map((item) => {
                  const duration = flowDurationLabel(draft.flow[item.stage], draft.flow_ends[item.stage]);
                  return (
                    <div className="flow-cell" key={item.stage}>
                      <strong>{item.label}</strong>
                      <label>ΈΝΑΡΞΗ
                        <input type="time" value={draft.flow[item.stage] ?? ""} disabled={!canEdit} onChange={(event) => setFlowTime("flow", item.stage, event.target.value || null)} />
                      </label>
                      {canEdit ? <button type="button" className="now" onClick={() => setFlowTime("flow", item.stage, athensNow())}>Τώρα</button> : null}
                      <label>ΤΕΛΟΣ
                        <input type="time" value={draft.flow_ends[item.stage] ?? ""} disabled={!canEdit} onChange={(event) => setFlowTime("flow_ends", item.stage, event.target.value || null)} />
                      </label>
                      {canEdit ? <button type="button" className="now" onClick={() => setFlowTime("flow_ends", item.stage, athensNow())}>Τώρα</button> : null}
                      {duration ? <p className="flow-duration">{duration}</p> : null}
                    </div>
                  );
                })}
              </div>
            </article>
          ) : null}

          {step < 3 ? (
            <div className="lead-sections">
              <article className="panel">
                <h2>1) Στοιχεία ενδιαφερομένου</h2>
                {parties.map((party, index) => (
                  <div className="party-card" key={index}>
                    <div className="fields">
                      <div className="party-name">
                        <label>Όνομα
                          <input value={party.given_name} disabled={!canEdit} onChange={(event) => setParty(index, { given_name: event.target.value })} />
                        </label>
                        {step === 2 ? (
                          <label className="party-came">
                            <input type="checkbox" checked={party.came} disabled={!canEdit} onChange={(event) => setParty(index, { came: event.target.checked })} />
                            <span>Ήρθε</span>
                          </label>
                        ) : null}
                      </div>
                      <label>Επώνυμο
                        <input value={party.family_name} disabled={!canEdit} onChange={(event) => setParty(index, { family_name: event.target.value })} />
                      </label>
                      <label>Τηλέφωνο
                        <input type="tel" inputMode="tel" value={party.phone ?? ""} disabled={!canEdit} onChange={(event) => setParty(index, { phone: sanitizePhoneInput(event.target.value) || null })} />
                      </label>
                      <label>e-mail address
                        <input inputMode="email" value={party.email ?? ""} disabled={!canEdit} onChange={(event) => setParty(index, { email: event.target.value || null })} />
                      </label>
                      <RelationField party={party} disabled={!canEdit} onChange={(relation) => setParty(index, { relation })} />
                    </div>
                    <label>Ειδικά αιτήματα
                      <textarea value={party.notes ?? ""} disabled={!canEdit} onChange={(event) => setParty(index, { notes: event.target.value || null })} />
                    </label>
                    {canEdit && parties.length > 1 ? <button type="button" className="secondary" onClick={() => setParties((current) => current.filter((_, partyIndex) => partyIndex !== index))}>Αφαίρεση</button> : null}
                  </div>
                ))}
                {canEdit ? <button type="button" className="secondary" onClick={() => setParties((current) => (current.length >= 8 ? current : [...current, emptyParty()]))}>+ Προσθήκη ενδιαφερομένου</button> : null}
              </article>

              <article className="panel">
                <h2>2) Στοιχεία γευστικής δοκιμής</h2>
                <div className="fields">
                  <label>Ημερομηνία γευστικής δοκιμής
                    <input type="date" value={draft.tasted_on ?? ""} disabled={!canEdit} onChange={(event) => patch({ tasted_on: event.target.value || null })} />
                  </label>
                  <label>Ώρα άφιξης
                    <input type="time" value={draft.arrival_time ?? ""} disabled={!canEdit} onChange={(event) => patch({ arrival_time: event.target.value || null })} />
                  </label>
                  <label>Επαναληπτική επίσκεψη
                    <input type="date" value={draft.revisit_on ?? ""} disabled={!canEdit} onChange={(event) => patch({ revisit_on: event.target.value || null })} />
                  </label>
                </div>
              </article>

              <article className="panel">
                <h2>3) Ημερομηνίες ενδιαφέροντος</h2>
                <div className="interest-list">
                  {visibleInterestDates(draft.interest_dates).map((row, index) => (
                    <div className="interest-row" key={index}>
                      <label>{interestDateLabel(index, visibleInterestDates(draft.interest_dates).length)}
                        <input type="date" value={row.date} disabled={!canEdit} onChange={(event) => setInterestDate(index, event.target.value)} />
                      </label>
                      <Choice label="μεσημέρι" pressed={row.slot === "midday"} disabled={!canEdit || !row.date} onClick={() => setSlot(index, row.slot === "midday" ? null : "midday")} />
                      <Choice label="βράδυ" pressed={row.slot === "evening"} disabled={!canEdit || !row.date} onClick={() => setSlot(index, row.slot === "evening" ? null : "evening")} />
                      {canEdit && index > 0 ? <button type="button" className="secondary" onClick={() => removeInterestDate(index)}>Αφαίρεση</button> : null}
                      <ConflictHint conflicts={row.date ? conflicts.get(row.date) : undefined} venue={draft.venue_choice} slot={row.date ? conflictSlots.get(row.date) : null} />
                    </div>
                  ))}
                </div>
                {canEdit ? <button type="button" className="secondary" onClick={addInterestDate}>+ Προσθήκη εναλλακτικής ημερομηνίας</button> : null}
              </article>

              <ClashSuggestions suggestions={suggestions} rows={visibleInterestDates(draft.interest_dates)} venue={draft.venue_choice} canEdit={canEdit} onPick={applySuggestion} />

              <article className="panel">
                <h2>4) Χώρος & Καλεσμένοι</h2>
                <div className="venue-picks">
                  {VENUES.map((venue) => {
                    const selected = draft.venue_choice === venue.value;
                    return (
                      <div className="venue-pick" key={venue.value}>
                        <Choice label={venue.label} pressed={selected} disabled={!canEdit} onClick={() => patch({ venue_choice: selected ? null : venue.value })} />
                        {selected ? <label className="guest-count">Καλεσμένοι<input type="number" inputMode="numeric" min={1} step={1} value={guestRaw} disabled={!canEdit} onChange={(event) => onGuests(event.target.value)} /></label> : null}
                      </div>
                    );
                  })}
                </div>
                {draft.venue_choice == null ? <label>Καλεσμένοι<input type="number" inputMode="numeric" min={1} step={1} value={guestRaw} disabled={!canEdit} onChange={(event) => onGuests(event.target.value)} /></label> : null}
                {draft.venue_choice === "other" ? <label>Άλλος χώρος<input value={draft.venue_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ venue_other: event.target.value || null })} /></label> : null}
                <VenueConflictWarning conflicts={conflicts} venue={draft.venue_choice} guests={draft.guest_count} slots={conflictSlots} />
              </article>

              <article className="panel">
                <h2>5) Γεγονός</h2>
                <div className="choice-row">
                  {OCCASION_CHOICES.map(([value, label]) => (
                    <Choice key={value} label={label} pressed={draft.occasion === value} disabled={!canEdit} onClick={() => patch({ occasion: draft.occasion === value ? null : value })} />
                  ))}
                  {extraOccasion ? <Choice label={eventTypeLabel(extraOccasion) || extraOccasion} pressed disabled={!canEdit} onClick={() => patch({ occasion: null })} /> : null}
                </div>
                {draft.occasion === "other" ? <label className="other-field">Άλλο<input value={draft.occasion_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ occasion_other: event.target.value || null })} /></label> : null}
                <h3>Τύπος εκδήλωσης</h3>
                <div className="choice-row">
                  {STYLES.map((option) => (
                    <Choice key={option.value} label={option.label} pressed={draft.service_style === option.value} disabled={!canEdit} onClick={() => patch({ service_style: draft.service_style === option.value ? null : option.value })} />
                  ))}
                </div>
                {draft.service_style === "other" ? <label className="other-field">Άλλο<input value={draft.service_style_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ service_style_other: event.target.value || null })} /></label> : null}
              </article>

              <article className="panel">
                <h2>6) MENU – ΕΚΚΛΗΣΙΑ - DJ</h2>
                <div className="choice-row">
                  {MENUS.map((option) => (
                    <Choice key={option.value} label={option.label} pressed={draft.menu_choice === option.value} disabled={!canEdit} onClick={() => patch({ menu_choice: draft.menu_choice === option.value ? null : option.value })} />
                  ))}
                </div>
                {draft.menu_choice === "other" ? <label className="other-field">Άλλο μενού<input value={draft.menu_other ?? ""} disabled={!canEdit} onChange={(event) => patch({ menu_other: event.target.value || null })} /></label> : null}
                <h3>Εκκλησία</h3>
                <div className="choice-row">
                  <Choice label="Εντός" pressed={draft.church === "on_site"} disabled={!canEdit} onClick={() => patch({ church: toggle(draft.church, "on_site") })} />
                  <Choice label="Εκτός" pressed={draft.church === "off_site"} disabled={!canEdit} onClick={() => patch({ church: toggle(draft.church, "off_site") })} />
                </div>
                {draft.church === "off_site" ? <label>Τοποθεσία<input value={draft.church_place ?? ""} disabled={!canEdit} onChange={(event) => patch({ church_place: event.target.value || null })} /></label> : null}
                <DjChoices value={draft.dj} disabled={!canEdit} onChange={(dj) => patch({ dj })} />
                <YesNo label="Ορχήστρα" value={draft.orchestra} disabled={!canEdit} onChange={(orchestra) => patch({ orchestra })} />
                <YesNo label="Πυροτεχνήματα" value={draft.fireworks} disabled={!canEdit} onChange={(fireworks) => patch({ fireworks })} />
                <YesNo label="Ανιματέρ" value={draft.animator} disabled={!canEdit} onChange={(animator) => patch({ animator })} />
                <YesNo label="Παιχνίδια" value={draft.games} disabled={!canEdit} onChange={(games) => patch({ games })} />
                <label className="other-field">Άλλο<input value={draft.extras ?? ""} disabled={!canEdit} onChange={(event) => patch({ extras: event.target.value || null })} /></label>
              </article>

              <article className="panel">
                <h2>EVLTN</h2>
                <div className="choice-row">
                  {RATINGS.map((rating) => (
                    <Choice key={rating.value} label={rating.label} hint={rating.hint} pressed={draft.evaluation === rating.value} disabled={!canEdit} onClick={() => {
                      const next = draft.evaluation === rating.value ? null : rating.value;
                      patch({ evaluation: next, evaluation_note: next === "OTHER" ? draft.evaluation_note : null });
                    }} />
                  ))}
                </div>
                {draft.evaluation === "OTHER" ? <label>Άλλο<input value={draft.evaluation_note ?? ""} disabled={!canEdit} onChange={(event) => patch({ evaluation_note: event.target.value || null })} /></label> : null}
                <label className="follow-up">
                  <span>{draft.follow_up ? followUpCaption(draft.follow_up) : "Follow-up"}</span>
                  <input type="date" value={draft.follow_up ?? ""} disabled={!canEdit} onChange={(event) => patch({ follow_up: event.target.value || null })} />
                </label>
              </article>
            </div>
          ) : null}

          {step === 3 ? (
            <MenuMail thanks={thanks} brand={houseBrand(draft.venue_choice)} canSend={canEdit && Boolean(cardId)} sending={sendingMail} onSend={() => void sendNow()} />
          ) : null}

          {step === 3 ? (
            <article className="panel lead-step-3">
              <h2>Συμφωνία</h2>
              <div className="fields">
                <label>Δέσμευση
                  <input type="date" value={agreement.commitment ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, commitment: event.target.value || null }))} />
                </label>
                <label>Ημερομηνία συμφωνητικού
                  <input type="date" value={agreement.contract_date ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, contract_date: event.target.value || null }))} />
                </label>
                <label>Ημερομηνία προκαταβολής
                  <input type="date" value={agreement.deposit_date ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, deposit_date: event.target.value || null }))} />
                </label>
                <label>Ημερομηνία εκδήλωσης
                  <input type="date" value={agreement.event_date ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, event_date: event.target.value || null }))} />
                </label>
                <label>Τύπος εκδήλωσης
                  <select value={typeValue} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, event_type: event.target.value || null }))}>
                    <option value="">—</option>
                    {TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    {typeValue && !knownType ? <option value={typeValue}>{eventTypeLabel(typeValue) || typeValue}</option> : null}
                  </select>
                </label>
                <label>Χώρος
                  <select value={agreement.venue_id ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, venue_id: event.target.value || null }))}>
                    <option value="">—</option>
                    {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
                  </select>
                </label>
                <label>Κωδικός φακέλου
                  <input value={agreement.file_code ?? ""} disabled={!canEdit} onChange={(event) => setAgreement((current) => ({ ...current, file_code: event.target.value || null }))} />
                </label>
                <label>Άτομα
                  <input type="number" inputMode="numeric" min={1} step={1} value={peopleRaw} disabled={!canEdit} onChange={(event) => onPeople(event.target.value)} />
                </label>
              </div>
            </article>
          ) : null}

          {step === 4 ? (
            <>
              <GuestFinalize
                visitId={cardId}
                canEdit={canEdit}
                suggestedCount={agreement.guests ?? draft.guest_count}
                suggestedMenu={suggestedGuestMenu()}
                venueName={contractSource().venueName ?? ""}
                onSent={refreshEmails}
              />
              <ContractForm visitId={cardId} canEdit={canEdit} source={contractSource()} onSent={refreshEmails} />
            </>
          ) : null}

          <div className="tasting-bar">
            <p className="save-state" aria-live="polite">{phase === "saving" ? "Αποθήκευση…" : phase === "saved" && !dirty ? "Αποθηκεύτηκε" : ""}</p>
            <div className="actions">
              {step > 1 ? <button type="button" className="secondary" onClick={() => goToStep((step - 1) as LeadStep)}>Πίσω</button> : null}
              {step < progress ? <button type="button" className="secondary" onClick={() => goToStep((step + 1) as LeadStep)}>Συνέχεια</button> : null}
              {canEdit && step < 4 ? <button type="button" onClick={() => void save()} disabled={phase === "saving"}>Αποθήκευση</button> : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  function suggestedGuestMenu(): string {
    if (thanks?.menu) return thanks.menu;
    if (draft.menu_choice === "comfort" || draft.menu_choice === "social" || draft.menu_choice === "nagia") return draft.menu_choice;
    return (draft.menu_other ?? "").trim();
  }

  function contractSource() {
    const venueFromAgreement = venues.find((venue) => venue.id === agreement.venue_id)?.name ?? null;
    const venueFromChoice = draft.venue_choice === "other" ? draft.venue_other : VENUES.find((venue) => venue.value === draft.venue_choice)?.label ?? null;
    const serviceLabel = draft.service_style === "buffet" ? "Μπουφέ" : draft.service_style === "cocktail" ? "Cocktail" : draft.service_style === "other" ? draft.service_style_other : null;
    const menuLabel = draft.menu_choice === "comfort" ? "Μενού Α" : draft.menu_choice === "social" ? "Μενού Β" : draft.menu_choice === "nagia" ? "Μενού Γ" : draft.menu_choice === "other" ? draft.menu_other : null;
    const ceremony = draft.church === "on_site" ? "Μυστήριο εντός του χώρου" : draft.church === "off_site" ? `Μυστήριο εκτός${draft.church_place ? `, ${draft.church_place}` : ""}` : null;
    return {
      parties,
      contractDate: agreement.contract_date,
      depositDate: agreement.deposit_date,
      eventDate: agreement.event_date,
      eventTypeLabel: eventTypeLabel(agreement.event_type) || null,
      venueName: venueFromAgreement || venueFromChoice,
      guests: agreement.guests ?? draft.guest_count,
      serviceLabel,
      menuLabel,
      ceremony,
      today: civilToday("Europe/Athens"),
    };
  }

  function goToStep(next: LeadStep) {
    if (next > progressRef.current) return;
    setStep(next);
    window.scrollTo({ top: 0 });
  }

  function setSlot(index: number, slot: InterestDate["slot"]) {
    setDraft((current) => ({ ...current, interest_dates: visibleInterestDates(current.interest_dates).map((item, itemIndex) => (itemIndex === index ? { ...item, slot } : item)) }));
  }

  function applySuggestion(pick: SuggestionPick) {
    setDraft((current) => {
      const base = pick.venue ? applyFollowUp(current, { venue_choice: pick.venue, venue_other: null }) : current;
      return {
        ...base,
        interest_dates: visibleInterestDates(base.interest_dates).map((row, rowIndex) => (rowIndex === pick.index ? { ...row, ...(pick.date ? { date: pick.date } : {}), ...(pick.slot !== undefined ? { slot: pick.slot } : {}) } : row)),
      };
    });
  }

  function setInterestDate(index: number, date: string) {
    setDraft((current) => ({ ...current, interest_dates: visibleInterestDates(current.interest_dates).map((item, itemIndex) => (itemIndex === index ? { ...item, date } : item)) }));
  }

  function addInterestDate() {
    setDraft((current) => ({ ...current, interest_dates: [...visibleInterestDates(current.interest_dates), { date: "", slot: null }] }));
  }

  function removeInterestDate(index: number) {
    if (index === 0) return;
    setDraft((current) => ({ ...current, interest_dates: visibleInterestDates(current.interest_dates).filter((_, rowIndex) => rowIndex !== index) }));
  }

  function setFlowTime(key: "flow" | "flow_ends", stage: FlowStage, value: string | null) {
    setDraft((current) => ({ ...current, [key]: { ...current[key], [stage]: value } }));
  }
}

function MenuMail({ thanks, brand, canSend, sending, onSend }: {
  thanks: ThanksStatus | null;
  brand: string | null;
  canSend: boolean;
  sending: boolean;
  onSend: () => void;
}) {
  const mail = thanksMessage(brand, "ο σύνδεσμος επιλογής");
  const sent = thanks?.state === "sent";
  return (
    <article className="panel thanks-status">
      <h2>Mail μετά τη γευστική</h2>
      <p className="meta">Δείγμα. Στο mail που φεύγει, ο σύνδεσμος ανοίγει την επιλογή Α, Β ή Γ.</p>
      <div className="mail-demo">
        <strong>{mail.subject}</strong>
        {mail.text.split("\n").filter((line) => line.length > 0).map((line) => <p key={line}>{line}</p>)}
        <div className="mail-menus">
          {THANKS_MENUS.map((menu) => <span key={menu.value}>{menu.label}</span>)}
        </div>
      </div>
      {sent ? <p>στάλθηκε</p> : thanks ? <p>προγραμματισμένο για {thanks.due_label}. Μπορεί να φύγει νωρίτερα.</p> : <p>Δεν έχει φύγει ακόμα. Η αποστολή το στέλνει τώρα.</p>}
      {thanks?.menu_label ? <p>επιλογή μενού: {thanks.menu_label}</p> : null}
      {canSend && !sent ? <button type="button" onClick={onSend} disabled={sending}>{sending ? "Αποστολή…" : "Αποστολή"}</button> : null}
    </article>
  );
}

function DjChoices({ value, disabled, onChange }: { value: DjChoice | null; disabled?: boolean; onChange: (next: DjChoice | null) => void }) {
  return (
    <>
      <h3>DJ</h3>
      <div className="choice-row">
        {DJ_CHOICES.map((choice) => (
          <Choice key={choice} label={DJ_LABELS[choice]} pressed={value === choice} disabled={disabled} onClick={() => onChange(value === choice ? null : choice)} />
        ))}
      </div>
    </>
  );
}

function YesNo({ label, value, disabled, onChange }: { label: string; value: boolean | null; disabled?: boolean; onChange: (next: boolean | null) => void }) {
  return (
    <>
      <h3>{label}</h3>
      <div className="choice-row">
        <Choice label="Ναι" pressed={value === true} disabled={disabled} onClick={() => onChange(value === true ? null : true)} />
        <Choice label="Όχι" pressed={value === false} disabled={disabled} onClick={() => onChange(value === false ? null : false)} />
      </div>
    </>
  );
}

function RelationField({ party, disabled, onChange }: { party: InterestedParty; disabled: boolean; onChange: (relation: string | null) => void }) {
  const derived = relationSelection(party.relation);
  const [otherOpen, setOtherOpen] = useState(derived.choice === "other");
  useEffect(() => {
    if (derived.choice === "other") setOtherOpen(true);
    else if (derived.choice) setOtherOpen(false);
  }, [derived.choice]);
  const isOther = otherOpen || derived.choice === "other";
  return (
    <>
      <label>Σχέση
        <select
          className="relation-select"
          value={isOther ? "other" : derived.choice}
          disabled={disabled}
          onChange={(event) => {
            const next = event.target.value;
            if (next === "other") {
              setOtherOpen(true);
              onChange(null);
              return;
            }
            setOtherOpen(false);
            onChange(relationStored(next));
          }}
        >
          <option value="">—</option>
          {RELATION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
          <option value="other">Άλλο</option>
        </select>
      </label>
      {isOther ? (
        <label>Άλλο
          <input value={derived.other} disabled={disabled} onChange={(event) => onChange(event.target.value || null)} />
        </label>
      ) : null}
    </>
  );
}

function Choice({ label, hint, pressed, disabled, onClick }: { label: string; hint?: string; pressed: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className="choice" aria-pressed={pressed} disabled={disabled} onClick={onClick}>
      <span>{label}</span>
      {hint ? <small>{hint}</small> : null}
    </button>
  );
}

function toggle(current: ChurchChoice | null, value: ChurchChoice): ChurchChoice | null {
  return current === value ? null : value;
}

function partyFrom(draft: TastingDraft): InterestedParty {
  return { given_name: draft.given_name, family_name: draft.family_name, phone: draft.phone, email: draft.email, relation: null, notes: draft.special_requests, came: false };
}

function pick(visit: Visit): TastingDraft {
  return {
    tasted_on: visit.tasted_on,
    arrival_time: visit.arrival_time,
    revisit_on: visit.revisit_on,
    interest_dates: visit.interest_dates,
    venue_choice: visit.venue_choice,
    venue_other: visit.venue_other,
    guest_count: visit.guest_count,
    occasion: visit.occasion,
    occasion_other: visit.occasion_other,
    service_style: visit.service_style,
    service_style_other: visit.service_style_other,
    menu_choice: visit.menu_choice,
    menu_other: visit.menu_other,
    church: visit.church,
    church_place: visit.church_place,
    dj: visit.dj,
    orchestra: visit.orchestra,
    fireworks: visit.fireworks,
    animator: visit.animator,
    games: visit.games,
    extras: visit.extras,
    given_name: visit.given_name,
    family_name: visit.family_name,
    phone: visit.phone,
    email: visit.email,
    special_requests: visit.special_requests,
    table_number: visit.table_number,
    flow: visit.flow ?? emptyFlow(),
    flow_ends: visit.flow_ends ?? emptyFlow(),
    evaluation: visit.evaluation,
    evaluation_note: visit.evaluation_note,
    follow_up: visit.follow_up,
    follow_up_manual: visit.follow_up_manual,
  };
}

/** A saved card opens at its own step; an event without one opens at the step its sales stage maps to. */
function leadStepOf(result: LeadPayload): LeadStep {
  if (result.visit) return cardStepOf(result.visit.step, result.visit.step);
  return eventCardStep(null, result.event?.sales_stage);
}

function blankLead(): LeadPayload {
  return { display_subject: "Νέα εκδήλωση", event_version: null, event: null, snapshot: emptySnapshot(), visit: null };
}

function sheetToLead(sheet: Sheet): LeadPayload {
  return {
    display_subject: "Νέα εκδήλωση",
    event_version: sheet.event_version,
    event: sheet.event,
    snapshot: sheet.snapshot,
    visit: sheet.visit,
  };
}

function deriveAgreement(draft: TastingDraft, snapshot: CrmSnapshot, venues: { id: string; name: string }[]): LeadAgreement {
  return agreementFromSources({
    workbook: snapshot.workbook,
    startDate: snapshot.start_date,
    eventType: snapshot.event_type,
    venueId: snapshot.venue_id,
    adults: snapshot.adults,
    interestDate: draft.interest_dates.find((row) => row.date)?.date ?? null,
    occasion: draft.occasion,
    guestCount: draft.guest_count,
    venues: venues.length ? venues : undefined,
    venueHint: venueHint(draft.venue_choice),
  });
}

function venueHint(choice: VenueChoice | null): string | null {
  if (choice === "casa") return "casa";
  if (choice === "ktema") return "ktima";
  if (choice === "regina") return "regina";
  return null;
}

function sortVenues(venues: { id: string; name: string }[]): { id: string; name: string }[] {
  return [...venues].sort((left, right) => venueRank(left.name) - venueRank(right.name) || left.name.localeCompare(right.name, "el"));
}

function venueRank(name: string): number {
  const folded = foldVenue(name);
  if (folded.includes("casa")) return 0;
  if (folded.includes("kthma") || folded.includes("ktima") || folded.includes("kτημα") || folded.includes("κτημα")) return 1;
  if (folded.includes("regina")) return 2;
  return 3;
}

function athensNow(): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Athens", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
  let hour = parts.find((part) => part.type === "hour")?.value ?? "00";
  if (hour === "24") hour = "00";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "00";
  return `${hour.slice(-2)}:${minute}`;
}
